import { Collapse, Form, Button, Divider, Space } from '@bedrock/components'
import { FileWordO } from '@bedrock/icons-react';
import DescriptionsFormItem from '@mpc/common/src/components/DescriptionsFormItem'
import CollapseTablePanel from '@mpc/common/src/components/CollapseTablePanel'
import React, { useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import TaxRateInputNumber from "@mpc/common/src/features/NewPendingQuote/TaxRate";
import QuoteFileDetail from '@mpc/common/src/features/NewPendingQuote/QuoteFileDetail';
import { currencyOptions } from "@mpc/common/src/utils/number";
import ClosingReportBtn from '#features/MPCDemand/ClosingReportTable/ClosingReportBtn';
import classnames from 'classnames/bind';
import { ajax } from 'utils';
import styles from './index.less';
const cx = classnames.bind(styles);

interface Props {
  detail: any;
  planInfo: any;
  onChange?: (values: any) => void;
  templateDownloadUrl: string; // 结案附件模版地址
  editType?: 'making' | 'view';
}

const QuotationSummary = forwardRef((props: Props, ref) => {
  const { detail, planInfo, onChange, editType = 'view', templateDownloadUrl } = props;
  const [form] = Form.useForm();
  const [formData, setFormData] = React.useState<any>({});

  useImperativeHandle(ref, () => ({
    validateFields: () => {
      return form.validateFields();
    }
  }));

  useEffect(() => {
    let $total_quoted = 0;
    let $last_round_price = 0;
    let $supplier_settle_price = 0;
    detail?.demand_suppliers?.[0]?.demand_supplier_quotes?.forEach(v => {
      $total_quoted += (v?.discount_quote_price_with_tax || 0) * 100;
      $last_round_price += (v?.last_quote_price || v?.quote_price || 0) * 100;
    })
    const currency = detail?.demand_suppliers?.[0]?.demand_supplier_quotes?.[0]?.currency?.toLocaleLowerCase()
    planInfo?.cooperate_accounts?.map(v => {
      $supplier_settle_price += (v?.$supplier_settle_price?.value || 0) * 100;
    })
    const tempData = {
      remark: detail?.remark,
      ...detail?.demand_suppliers?.[0],
      currency,
      $total_quoted: {
        value: $total_quoted / 100,
        currency
      },
      $last_round_price: {
        value: $last_round_price / 100,
        currency
      },
      $supplier_settle_price: {
        value: $supplier_settle_price / 100,
        currency
      }
    }
    form.setFieldsValue(tempData);
    setFormData(tempData);
  }, [detail, planInfo]);

  const settleReportAttachRequired = useMemo(() => {
    const {
      structured,
      need_content_marketing_attach,
      content_marketing_case_structured_version,
      is_screenshot_done,
    } = detail

    let settleReportAttachRequired = true;
    if (structured && content_marketing_case_structured_version) {
      settleReportAttachRequired = !need_content_marketing_attach
      if (!is_screenshot_done) {
        // 是否全部上传截图（主平台/付费分发）为否，则要求整单结案报告必填
        settleReportAttachRequired = true
      }
    }
    return settleReportAttachRequired
  }, [detail])

  const items = [
    {
      key: "quote_type",
      label: "报价类型",
      formItemKey: "quote_type",
      componentClass: "radio",
      view: true,
      required: true,
      elementProps: {
        style: {
          marginTop: 0
        },
        options: [
          {
            name: "含税金额",
            value: 1
          },
          {
            name: "不含税金额",
            value: 0
          }
        ]
      }
    },
    {
      key: "tax_rate",
      label: "税率",
      formItemKey: "tax_rate",
      view: true,
      componentClass: "inputNumber",
      span: 1,
      required: true,
      render: () => {
        return <TaxRateInputNumber view />
      }
    },
    {
      key: "currency",
      label: "报价币种 ",
      formItemKey: "currency",
      view: true,
      componentClass: "select",
      required: true,
      span: 2,
      elementProps: {
        style: {
          width: "100%"
        },
        options: currencyOptions
      }
    },
    {
      key: "service_fee_mode",
      label: "是否收取服务费",
      formItemKey: "service_fee_mode",
      componentClass: "radio",
      view: true,
      required: true,
      span: formData?.service_fee_mode === 1 ? 1 : 2,
      elementProps: {
        style: {
          marginTop: 0
        },
        options: [
          {
            name: "是",
            value: 1
          },
          {
            name: "否",
            value: 0
          }
        ]
      }
    },
    {
      key: 'service_fee_rate',
      label: '服务费率',
      formItemKey: 'service_fee_rate',
      componentClass: 'inputNumber',
      view: true,
      span: 1,
      required: true,
      show: formData?.service_fee_mode === 1,
      render: () => {
        return <TaxRateInputNumber view />;
      },
    },
    {
      key: "$last_round_price",
      label: "报价总金额-变更前\n(含税 + 服务费)",
      formItemKey: "$last_round_price",
      componentClass: "currencyType",
      elementProps: {
        currencyColor: 'black'
      },
      required: true,
      span: 1,
      view: true
    },
    {
      key: "$total_quoted",
      label: "报价总金额-变更后\n(含税 + 服务费)",
      formItemKey: "$total_quoted",
      componentClass: "currencyType",
      required: true,
      span: 1,
      view: true
    },
    {
      key: 'remark',
      label: '报价变更原因',
      required: true,
      formItemKey: 'remark',
      componentClass: 'textarea',
      span: 2,
      view: true,
      show: !!formData?.remark,
    },
    {
      key: "merge_quote_attachs",
      label: "报价总明细",
      formItemKey: "merge_quote_attachs",
      required: false,
      span: 2,
      show: true,
      render: () => {
        return <QuoteFileDetail detail={detail} view isAll />;
      }
    },
    {
      key: "bid_attachs",
      label: "供应商比选方案及文件",
      className: "view_upload_BkvKx",
      formItemKey: "bid_attachs",
      componentClass: "upload",
      required: false,
      span: 2,
      view: true,
      elementProps: {
        tip: "单个文件不能超过50M",
        fileSizeLimit: 52428800
      }
    },
    {
      key: "$supplier_settle_price",
      label: "最终结算总金额(含税 + 服务费)",
      formItemKey: "$supplier_settle_price",
      componentClass: "currencyType",
      required: true,
      span: 1,
      view: true
    },
    {
      key: "quote_settle_reports",
      label: "结案总明细",
      formItemKey: "quote_settle_reports",
      required: true,
      span: 1,
      show: detail?.need_structured_settle_report,
      validateTrigger: false,
      watchChildFormValidate: true,
      render: () => {
        return <ClosingReportBtn detail={detail} view={editType !== 'making'} />;
      }
    },
    // {
    //   key: "settle_report_attachs",
    //   label: "结案报告",
    //   formItemKey: "settle_report_attachs",
    //   required: settleReportAttachRequired,
    //   span: 2,
    //   show: true,
    //   componentClass: 'upload',
    //   elementProps: {
    //     tip: <div>
    //       <Space size={[4, 0]}>
    //         <span>
    //           ( 单个文件不超过200M )
    //         </span>
    //         <Divider />
    //         <Button type="link"
    //           icon={<FileWordO />}
    //           onClick={() => {
    //             ajax({
    //               url:
    //                 templateDownloadUrl || 'outsource/sm/projects/download_case_template',
    //               method: 'POST',
    //               dataType: 'blob',
    //               body: {},
    //             });
    //         }}>
    //           结案报告模版下载
    //         </Button>
    //       </Space>
    //       {
    //         (detail?.structured && detail?.content_marketing_case_structured_version && !detail?.is_screenshot_done) ?
    //           <div className={cx('making-content-upload-tip-text')}>注：主平台/付费分发作品未上传作品截图，仍需上传结案报告。</div> :
    //           (
    //             detail?.$contentStructured && <div className={cx('making-content-upload-tip-text')}>注：本需求单中的达人合作数据，已自动获取您在内容营销系统上传的相关数据。因此，除差旅餐饮事项(如有，请在上述合作内容中上传)外，您无需提供结案报告。如有需要，您可以下载本需求的结案信息，感谢您的配合。</div>
    //           )
    //       }
    //     </div>,
    //     fileSizeLimit: 200 * 1024 * 1024
    //   },
    // }
  ]

  return (
    <Form
      form={form}
      onValuesChange={(changeValues, values) => {
        onChange?.(values)
      }}
    >
      {
        <Collapse
          activeKey={[1]}
        >
          <CollapseTablePanel
            showArrow={false}
            key={1}
            header={<div>报价汇总</div>}
          >
            <DescriptionsFormItem
              column={2}
              items={items}
            />
          </CollapseTablePanel>
        </Collapse>
      }
    </Form>
  )
})

export default QuotationSummary
